import React, { useEffect, useState } from 'react';
import { Phone, Heart, Clipboard, Mail, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { weddingData } from '../mockData';

const Contacts = () => {
  const { language } = useLanguage();
  const content = weddingData[language].contacts;
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const getIcon = (iconName) => {
    const iconProps = { size: 28, className: "text-lilac-600" };
    switch (iconName) {
      case 'heart': return <Heart {...iconProps} className="text-pink-500 fill-pink-500" />;
      case 'clipboard': return <Clipboard {...iconProps} />;
      case 'phone': return <Phone {...iconProps} className="text-gold-500" />;
      default: return <Phone {...iconProps} />;
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Header */}
      <section className="bg-gradient-to-br from-lilac-600 via-lilac-500 to-lilac-700 py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-gold-400 rounded-full filter blur-3xl animate-float" />
          <div className="absolute bottom-20 right-20 w-64 h-64 bg-lilac-300 rounded-full filter blur-3xl animate-float" style={{ animationDelay: '2s' }} />
        </div>

        <div className={`container mx-auto px-6 sm:px-8 lg:px-12 text-center relative z-10 transition-all duration-1000 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <Phone className="mx-auto text-gold-400 mb-8" size={64} />
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-playfair font-bold text-white mb-6">
            {content.title}
          </h1>
          <p className="text-xl text-white/90 font-montserrat max-w-3xl mx-auto">
            {content.subtitle}
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-gold-300 to-transparent mx-auto rounded-full mt-8" />
        </div>
      </section>

      {/* Intro */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-xl text-gray-700 font-montserrat leading-relaxed">
              {content.intro}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Categories */}
      <section className="py-20 bg-gradient-to-br from-lilac-50 via-white to-lilac-50">
        <div className="container mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto space-y-8">
            {content.categories.map((category, index) => (
              <div
                key={index}
                className={`card-premium transition-all duration-700 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                {/* Category Header */}
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-lilac-100 to-gold-50 rounded-2xl flex items-center justify-center shadow-md">
                    {getIcon(category.icon)}
                  </div>
                  <h2 className="text-2xl font-playfair font-bold text-lilac-700">
                    {category.title}
                  </h2>
                </div>

                {/* Contacts */}
                {category.contacts && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    {category.contacts.map((contact, contactIndex) => (
                      <div
                        key={contactIndex}
                        className="bg-gradient-to-br from-lilac-50 to-white rounded-xl p-5 border border-lilac-100 hover:border-gold-300 transition-all duration-300"
                      >
                        <h3 className="font-playfair font-bold text-lilac-700 text-lg mb-1">
                          {contact.name}
                        </h3>
                        <p className="text-gold-600 font-montserrat text-sm mb-3">
                          {contact.role}
                        </p>
                        {contact.email && (
                          <a
                            href={`mailto:${contact.email}`}
                            className="flex items-center space-x-2 text-lilac-600 hover:text-gold-600 transition-colors duration-300"
                          >
                            <Mail size={16} />
                            <span className="font-montserrat text-sm">{contact.email}</span>
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Note (for emergency section) */}
                {category.note && (
                  <div className="flex items-start space-x-3 bg-gold-50 border-l-4 border-gold-500 rounded-r-xl p-4">
                    <AlertTriangle className="text-gold-600 flex-shrink-0 mt-0.5" size={20} />
                    <p className="text-gray-700 font-montserrat text-sm italic">
                      {category.note}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Numbers */}
      <section className="py-20 bg-gradient-to-br from-lilac-600 via-lilac-500 to-lilac-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-40 h-40 bg-gold-400 rounded-full filter blur-3xl animate-float" />
        </div>

        <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-playfair font-bold text-white text-center mb-10">
              {content.emergency.title}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {content.emergency.numbers.map((item, index) => (
                <div
                  key={index}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 text-center hover:bg-white/20 transition-all duration-300"
                >
                  <p className="text-white/80 font-montserrat text-sm mb-2">
                    {item.name}
                  </p>
                  <p className="text-gold-300 font-playfair font-bold text-2xl">
                    {item.number}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contacts;
